import React, { useState, useEffect, useRef, useCallback } from "react";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import { Bell, X, RefreshCw, Pill, FileText, Calendar, ClipboardList } from "lucide-react";
import Skeleton from "@mui/material/Skeleton";
import useTitanApp from "../../../hooks/useTitanApp";
import {
  getPharmacyNotifications,
  markNotificationsAsRead,
  markNotificationAsRead,
} from "../../../dispatchers/pharmacyDashboardDispatchers";
import { notificationsHub } from "@/services/notificationsHub";
interface NotificationTypeConfig {
  label: string;
  icon: React.ElementType;
  color: string;
  showInBell: boolean;
  reviewRoute: string;
  buttonLabel: string;
}

const NOTIFICATION_TYPE_CONFIG: Record<number, NotificationTypeConfig> = {
  2: {
    label: "Appointment Booked",
    icon: Calendar,
    color: "blue",
    showInBell: true,
    reviewRoute: "schedule",
    buttonLabel: "View",
  },
  4: {
    label: "NHS 111 Referral",
    icon: Pill,
    color: "blue",
    showInBell: true,
    reviewRoute: "consultation",
    buttonLabel: "Review",
  },
  5: {
    label: "Prescription Approval",
    icon: FileText,
    color: "purple",
    showInBell: true,
    reviewRoute: "approvals",
    buttonLabel: "Review",
  },
  6: {
    label: "BaRS Referral",
    icon: Pill,
    color: "green",
    showInBell: true,
    reviewRoute: "consultation",
    buttonLabel: "Review",
  },
  7: {
    label: "Appointment Cancelled",
    icon: Calendar,
    color: "red",
    showInBell: true,
    reviewRoute: "schedule",
    buttonLabel: "View",
  },
  8: {
    label: "Appointment Booked",
    icon: Calendar,
    color: "blue",
    showInBell: true,
    reviewRoute: "schedule",
    buttonLabel: "View",
  },
  9: {
    label: "Appointment Rescheduled",
    icon: Calendar,
    color: "orange",
    showInBell: true,
    reviewRoute: "schedule",
    buttonLabel: "View",
  },
  10: {
    label: "Appointment Rescheduled",
    icon: Calendar,
    color: "orange",
    showInBell: true,
    reviewRoute: "schedule",
    buttonLabel: "View",
  },
  11: {
    label: "Task Assigned",
    icon: ClipboardList,
    color: "blue",
    showInBell: true,
    reviewRoute: "tasks",
    buttonLabel: "View",
  },
};

const BELL_NOTIFICATION_TYPES = Object.entries(NOTIFICATION_TYPE_CONFIG)
  .filter(([, config]) => config.showInBell)
  .map(([key]) => Number(key));

export default function NotificationBell() {
  const titanApp = useTitanApp();
  const { appState, pharmacyDashboardState } = titanApp;
  const { darkMode, currentPharmacyId } = appState || {};
  const { user } = (titanApp as any).auth || {};
  const userEmail = user?.email || "";
  const { pharmacyNotificationsData, isPharmacyNotificationsLoading } =
    pharmacyDashboardState || {};

  const [showDropdown, setShowDropdown] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const bellNotifications = Array.isArray(pharmacyNotificationsData)
    ? pharmacyNotificationsData.filter((n: any) =>
        BELL_NOTIFICATION_TYPES.includes(n.pharmacyNotificationMessageType),
      )
    : [];

  const unreadCount = bellNotifications.filter(
    (n: any) => !n.readBy?.includes(userEmail),
  ).length;

  useEffect(() => {
    if (currentPharmacyId) {
      getPharmacyNotifications(currentPharmacyId).then(() => {
        setInitialLoad(false);
      });
    }
  }, [currentPharmacyId]);

  useEffect(() => {
    if (!currentPharmacyId) return;

    let unsubscribe: (() => Promise<void>) | undefined;
    let isDisposed = false;

    (async () => {
      try {
        const cleanup = await notificationsHub.subscribeMergeResult(
          currentPharmacyId,
          async () => {
            if (isDisposed) return;
            await getPharmacyNotifications(currentPharmacyId);
          },
          () => {},
        );
        if (isDisposed) {
          await cleanup();
          return;
        }
        unsubscribe = cleanup;
      } catch (error) {
        console.error("[NotificationsHub] Subscription failed:", error);
      }
    })();

    return () => {
      isDisposed = true;
      unsubscribe?.();
    };
  }, [currentPharmacyId]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    }
    if (showDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showDropdown]);

  const refreshCooldownRef = useRef(false);

  const handleRefresh = useCallback(async () => {
    if (!currentPharmacyId || refreshCooldownRef.current) return;
    refreshCooldownRef.current = true;
    setIsRefreshing(true);
    await getPharmacyNotifications(currentPharmacyId);
    setIsRefreshing(false);
    setTimeout(() => {
      refreshCooldownRef.current = false;
    }, 5000);
  }, [currentPharmacyId]);

  const handleMarkAllAsRead = useCallback(async () => {
    if (!currentPharmacyId) return;
    await markNotificationsAsRead(currentPharmacyId);
    await getPharmacyNotifications(currentPharmacyId);
  }, [currentPharmacyId]);

  const handleMarkOneAsRead = useCallback(
    async (notificationId: string) => {
      await markNotificationAsRead(notificationId);
      if (currentPharmacyId) {
        await getPharmacyNotifications(currentPharmacyId);
      }
    },
    [currentPharmacyId],
  );

  const handleReview = useCallback(
    (notification: any) => {
      const config =
        NOTIFICATION_TYPE_CONFIG[notification.pharmacyNotificationMessageType];
      if (!config) return;

      if (!notification.readBy?.includes(userEmail) && notification._id) {
        handleMarkOneAsRead(notification._id);
      }
      setShowDropdown(false);
    },
    [userEmail, handleMarkOneAsRead],
  );

  function formatTimeAgo(timeAgo: string): string {
    return timeAgo || "";
  }

  const showSkeleton =
    (initialLoad && isPharmacyNotificationsLoading) || isRefreshing;

  return (
    <div className="relative" ref={dropdownRef}>
      <Button
        variant="ghost"
        size="sm"
        className="relative"
        onClick={() => setShowDropdown(!showDropdown)}
      >
        <Bell className="w-4 h-4" color={darkMode ? "#bdbdbd" : "#0a0a0a"} />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center leading-none">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </Button>

      {showDropdown && (
        <div
          className={`absolute top-full right-0 mt-2 w-96 rounded-lg shadow-lg border z-50 max-h-96 overflow-y-auto ${
            darkMode
              ? "bg-gray-800 border-gray-700"
              : "bg-white border-gray-200"
          }`}
        >
          <div
            className={`p-4 border-b ${
              darkMode ? "border-gray-700" : "border-gray-200"
            }`}
          >
            <div className="flex items-center justify-between">
              <h3
                className={`font-semibold ${
                  darkMode ? "text-white" : "text-gray-900"
                }`}
              >
                Notifications
              </h3>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleRefresh}
                  disabled={isRefreshing}
                  className={`p-1 ${
                    darkMode
                      ? "text-gray-400 hover:text-white"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  <RefreshCw
                    className={`w-3.5 h-3.5 ${isRefreshing ? "animate-spin" : ""}`}
                  />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowDropdown(false)}
                  className={`p-1 ${
                    darkMode
                      ? "text-gray-400 hover:text-white"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
            {unreadCount > 0 && (
              <p
                className={`text-sm mt-1 ${
                  darkMode ? "text-gray-400" : "text-gray-600"
                }`}
              >
                {unreadCount} new notification{unreadCount !== 1 ? "s" : ""}
              </p>
            )}
          </div>

          <div className="flex-1">
            {showSkeleton ? (
              <div className="p-4 space-y-4">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="flex items-start gap-3">
                    <Skeleton variant="circular" width={32} height={32} />
                    <div className="flex-1">
                      <Skeleton variant="text" width="60%" height={20} />
                      <Skeleton
                        variant="text"
                        width="90%"
                        height={16}
                        sx={{ mt: 0.5 }}
                      />
                      <Skeleton
                        variant="text"
                        width="30%"
                        height={14}
                        sx={{ mt: 0.5 }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            ) : bellNotifications.length === 0 ? (
              <div
                className={`p-8 text-center text-sm ${
                  darkMode ? "text-gray-500" : "text-gray-400"
                }`}
              >
                No notifications
              </div>
            ) : (
              bellNotifications.map((notification: any) => {
                const config =
                  NOTIFICATION_TYPE_CONFIG[
                    notification.pharmacyNotificationMessageType
                  ];
                const Icon = config?.icon || FileText;
                const colorClass = config?.color || "gray";
                const isUnread = !notification.readBy?.includes(userEmail);
                const isCritical = notification.notificationStatusType === 3;

                return (
                  <div
                    key={notification._id}
                    className={`p-4 border-b cursor-pointer transition-colors ${
                      darkMode
                        ? "border-gray-700 hover:bg-gray-700"
                        : "border-gray-100 hover:bg-gray-50"
                    } ${isUnread ? (darkMode ? "bg-blue-900/10" : "bg-blue-50/50") : ""}`}
                    onClick={() => {
                      if (isUnread && notification._id) {
                        handleMarkOneAsRead(notification._id);
                      }
                    }}
                  >
                    <div className="flex items-start gap-3">
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                          colorClass === "blue"
                            ? darkMode
                              ? "bg-blue-900 text-blue-300"
                              : "bg-blue-100 text-blue-700"
                            : colorClass === "green"
                              ? darkMode
                                ? "bg-green-900 text-green-300"
                                : "bg-green-100 text-green-700"
                              : colorClass === "red"
                                ? darkMode
                                  ? "bg-red-900 text-red-300"
                                  : "bg-red-100 text-red-700"
                                : colorClass === "orange"
                                  ? darkMode
                                    ? "bg-orange-900 text-orange-300"
                                    : "bg-orange-100 text-orange-700"
                                  : colorClass === "purple"
                                    ? darkMode
                                      ? "bg-purple-900 text-purple-300"
                                      : "bg-purple-100 text-purple-700"
                                    : darkMode
                                      ? "bg-gray-700 text-gray-300"
                                      : "bg-gray-100 text-gray-700"
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h4
                            className={`font-medium text-sm truncate ${
                              darkMode ? "text-white" : "text-gray-900"
                            }`}
                          >
                            {notification.title}
                          </h4>
                          {isUnread && (
                            <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0"></div>
                          )}
                          {isCritical && (
                            <Badge
                              variant="destructive"
                              className="text-xs flex-shrink-0"
                            >
                              URGENT
                            </Badge>
                          )}
                        </div>
                        <p
                          className={`text-sm mt-1 truncate ${
                            darkMode ? "text-gray-400" : "text-gray-600"
                          }`}
                        >
                          {notification.message}
                        </p>
                        <div className="flex items-center justify-between mt-2">
                          <span
                            className={`text-xs ${
                              darkMode ? "text-gray-500" : "text-gray-500"
                            }`}
                          >
                            {formatTimeAgo(notification.timeAgo)}
                          </span>
                          {/* TODO: Uncomment after confirming button click behavior with client
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-xs h-6"
                            onClick={(e: React.MouseEvent) => {
                              e.stopPropagation();
                              handleReview(notification);
                            }}
                          >
                            {config?.buttonLabel || "Review"}
                          </Button>
                          */}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {bellNotifications.length > 0 && unreadCount > 0 && (
            <div
              className={`p-3 border-t ${
                darkMode ? "border-gray-700" : "border-gray-200"
              }`}
            >
              <Button
                variant="ghost"
                size="sm"
                className="w-full text-sm"
                onClick={handleMarkAllAsRead}
              >
                Mark all as read
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
